import { useEffect, useState } from 'react';

interface Props {
    searchText: string;
    passOutYear: number | null;
    programId: number | null;
    batchOptions: number[];
    programOptions: { id: number; name: string }[];
    hasActiveFilters: boolean;
    onSearchTextChange: (v: string) => void;
    onSearchCommit: (v: string) => void;
    onBatchChange: (v: number | null) => void;
    onProgramChange: (v: number | null) => void;
    onClearAll: () => void;
}

function useDebouncedValue<T>(value: T, delayMs = 300): T {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setDebounced(value), delayMs);
        return () => clearTimeout(t);
    }, [value, delayMs]);
    return debounced;
}

const BirthdayFilters = ({
    searchText,
    passOutYear,
    programId,
    batchOptions,
    programOptions,
    hasActiveFilters,
    onSearchTextChange,
    onSearchCommit,
    onBatchChange,
    onProgramChange,
    onClearAll,
}: Props) => {
    const debounced = useDebouncedValue(searchText, 300);

    useEffect(() => {
        onSearchCommit(debounced);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debounced]);

    const selectedProgramName = programId == null
        ? null
        : programOptions.find((p) => p.id === programId)?.name ?? null;

    return (
        <div className="bd-filters mb-3">
            <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                <div className="bd-search-wrapper position-relative flex-1">
                    <input
                        type="text"
                        className="form-control bd-search-input"
                        placeholder="Search by name, company, or city"
                        value={searchText}
                        onChange={(e) => onSearchTextChange(e.target.value)}
                    />
                    {searchText !== '' && (
                        <button
                            type="button"
                            className="bd-search-clear"
                            aria-label="Clear search"
                            onClick={() => onSearchTextChange('')}>
                            ×
                        </button>
                    )}
                </div>

                <select
                    className="form-select bd-select"
                    value={passOutYear ?? ''}
                    onChange={(e) => onBatchChange(e.target.value === '' ? null : Number(e.target.value))}>
                    <option value="">All batches</option>
                    {batchOptions.map((year) => (
                        <option key={year} value={year}>
                            {year}
                        </option>
                    ))}
                </select>

                <select
                    className="form-select bd-select"
                    value={programId ?? ''}
                    onChange={(e) => onProgramChange(e.target.value === '' ? null : Number(e.target.value))}>
                    <option value="">All programs</option>
                    {programOptions.map((p) => (
                        <option key={p.id} value={p.id}>
                            {p.name}
                        </option>
                    ))}
                </select>

                {hasActiveFilters && (
                    <button type="button" className="btn btn-link p-0 bd-clear-all" onClick={onClearAll}>
                        Clear all
                    </button>
                )}
            </div>

            {hasActiveFilters && (
                <div className="d-flex flex-wrap gap-2 bd-chips">
                    {searchText.trim() !== '' && (
                        <span className="bd-chip">
                            Search: "{searchText}"
                            <button
                                type="button"
                                className="bd-chip-x"
                                aria-label="Remove search"
                                onClick={() => onSearchTextChange('')}>
                                ×
                            </button>
                        </span>
                    )}
                    {passOutYear != null && (
                        <span className="bd-chip">
                            Batch: {passOutYear}
                            <button
                                type="button"
                                className="bd-chip-x"
                                aria-label="Remove batch filter"
                                onClick={() => onBatchChange(null)}>
                                ×
                            </button>
                        </span>
                    )}
                    {programId != null && selectedProgramName && (
                        <span className="bd-chip">
                            Program: {selectedProgramName}
                            <button
                                type="button"
                                className="bd-chip-x"
                                aria-label="Remove program filter"
                                onClick={() => onProgramChange(null)}>
                                ×
                            </button>
                        </span>
                    )}
                </div>
            )}
        </div>
    );
};

export default BirthdayFilters;
